import React from 'react';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

import Icon from './Icon';
import { C, FONT, SHADOW, APPBAR_HEIGHT } from './tokens';

// The keydown handler (AppShell) accepts both Cmd and Ctrl; the chip just shows
// whichever the visiting user's platform actually uses.
const IS_MAC = typeof navigator !== 'undefined'
  && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');
const SHORTCUT_LABEL = IS_MAC ? '⌘K' : 'Ctrl K';

const crumbLinkSx = {
  whiteSpace: 'nowrap', textDecoration: 'none', color: 'inherit', cursor: 'pointer',
  '&:hover': { color: C.turquoiseText, textDecoration: 'underline' },
  '&:focus-visible': { outline: `2px solid ${C.turquoise}`, outlineOffset: 2, borderRadius: '3px' },
};

const crumbCurrentSx = { color: C.text, fontWeight: 700, whiteSpace: 'nowrap' };

// The controllers send Polaris' back-button label, which says nothing in a trail.
const GENERIC_BACK = ['atras', 'atrás', 'back', 'volver', 'regresar'];

// Builds the header trail: nav group, the resource's index, any real step the page named, then the
// page as plain text. A page's server crumbs are its ANCESTORS (Polaris' back target), so each is a
// link; reading the last as "the current page" is what left pages showing a dead "Atras".
export function buildTrail(active, pageTrail) {
  const trail = [];
  const push = (label, url, current = false) => {
    if (!label) return;
    // A repeat adds a step without adding information; keep the one with a url, and
    // let it inherit "this is the page" so a listing does not link to itself.
    const previous = trail[trail.length - 1];
    if (previous && previous.label === label) {
      if (url && !previous.url) previous.url = url;
      if (current) previous.current = true;
      return;
    }
    trail.push({ label, url, current });
  };

  if (active.groupName !== active.leafName) push(active.groupName, active.groupUrl);
  push(active.leafName, active.leafUrl);

  (pageTrail?.crumbs || []).forEach((crumb) => {
    const label = String(crumb.label || '').trim();
    if (!GENERIC_BACK.includes(label.toLowerCase())) push(label, crumb.url);
  });

  // The page always closes its own trail. A listing publishing its nav leaf's name dedupes into it;
  // a page with no title — one titled by a node — leaves the last ancestor as the last crumb.
  push(pageTrail?.title, undefined, true);

  return trail.length ? trail : [{ label: 'Delivery' }];
}

const iconBtnSx = {
  width: 30, height: 30, borderRadius: '7px', display: 'grid', placeItems: 'center',
  color: C.textMuted, cursor: 'pointer', background: 'none', border: 0, flex: 'none',
  '&:hover': { bgcolor: C.borderSoft, color: C.text },
  '&:focus-visible': { outline: `2px solid ${C.turquoise}`, outlineOffset: 2 },
};

const TopBar = ({
  expanded, railWidth, active, pageTrail, onToggleRail, onOpenPalette, searchPlaceholder, extra,
}) => (
  <Box
    component="header"
    sx={{
      height: APPBAR_HEIGHT, flex: 'none', bgcolor: '#fff',
      borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center',
      gap: '14px', px: '20px', position: 'fixed', top: 0, right: 0, left: railWidth,
      transition: 'left 200ms cubic-bezier(.2,0,0,1)', zIndex: (t) => t.zIndex.drawer - 1,
    }}
  >
    {!expanded && (
      <Tooltip title="Expandir el rail" placement="bottom" arrow>
        <Box component="button" onClick={onToggleRail} aria-label="Expandir el rail" sx={iconBtnSx}>
          <Icon name="panel-left-open" size={19} />
        </Box>
      </Tooltip>
    )}

    {/* Breadcrumb. Prefer the page's own server-provided trail (with real URLs
        back to its listing); fall back to the nav-derived group › module crumb. */}
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: '7px', minWidth: 0,
      font: `400 12px/1 ${FONT}`, color: C.textMuted,
    }}>
      {buildTrail(active, pageTrail).map((crumb, i, arr) => (
        <React.Fragment key={`${crumb.label}-${i}`}>
          {i > 0 && <Icon name="chevron-right" size={13} color={C.textMuted} />}
          {/* Only the page itself is plain text. The last crumb is styled as the
              current one either way, so a page that published no title still shows
              its closest ancestor — and that ancestor stays clickable. */}
          {crumb.url && !crumb.current ? (
            <Box
              component="a"
              href={crumb.url}
              sx={i === arr.length - 1 ? { ...crumbLinkSx, ...crumbCurrentSx } : crumbLinkSx}
            >
              {crumb.label}
            </Box>
          ) : (
            <Box component="span" sx={i === arr.length - 1 ? crumbCurrentSx : { whiteSpace: 'nowrap' }}>
              {crumb.label}
            </Box>
          )}
        </React.Fragment>
      ))}
    </Box>

    {/* ⌘K search */}
    <Box
      component="button"
      onClick={onOpenPalette}
      aria-label={searchPlaceholder}
      sx={{
        ml: 'auto', flex: 1, maxWidth: 420, height: 34, border: `1px solid ${C.border}`,
        borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', px: '12px',
        color: C.textFaint, cursor: 'pointer', bgcolor: '#fff',
        '&:hover': { borderColor: C.turquoise, boxShadow: SHADOW.focusRing },
        '&:focus-visible': { outline: 'none', borderColor: C.turquoise, boxShadow: SHADOW.focusRing },
      }}
    >
      <Icon name="search" size={15} color={C.textFaint} />
      <Box component="span" sx={{
        font: `400 12px/1 ${FONT}`, flex: 1, textAlign: 'left', whiteSpace: 'nowrap',
        overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {searchPlaceholder}
      </Box>
      <Box component="span" sx={{
        font: `700 10px/1 ${FONT}`, border: `1px solid ${C.border}`, borderRadius: '4px',
        p: '4px 5px', whiteSpace: 'nowrap',
      }}>{SHORTCUT_LABEL}</Box>
    </Box>

    {/* Slot for controls that belong to this app alone. Delivery leaves it empty:
        each country runs its own deployment, so there is no region to switch. */}
    {extra}
  </Box>
);

export default TopBar;
